/**
 * Every photo is generated at build time as AVIF + WebP + JPEG across four
 * widths, so phones download a ~40KB hero instead of a 1920px JPEG.
 * `pictures` feeds <AppImage>; `images` keeps plain URLs for schema.org and
 * anywhere a single src string is required.
 */
import hero from "@/assets/hero-panel-wide.jpg?w=640;960;1440;1920&format=avif;webp;jpg&as=picture";
import heroDaylight from "@/assets/hero-panel-daylight.jpg?w=640;960;1440;1920&format=avif;webp;jpg&as=picture";
import heroAlt from "@/assets/hero-panel-install.jpg?w=640;960;1440;1920&format=avif;webp;jpg&as=picture";
// In-page photos never render wider than a column, so 1280 is the top rung.
import interior from "@/assets/panel-interior.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import ev from "@/assets/ev-charger.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import fuse from "@/assets/old-fuse-box.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import upgrade from "@/assets/panel-upgrade.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import burke from "@/assets/burke-epo-president-master-electrician-denver.jpg?w=640;720;960;1080;1280&format=avif;webp;jpg&as=picture";
import team from "@/assets/epo-team.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import galleryBefore from "@/assets/gallery-before.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import galleryAfter from "@/assets/gallery-after.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import meterMast from "@/assets/meter-mast.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import conduit from "@/assets/underground-conduit.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import repair from "@/assets/panel-repair.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
import solarAc from "@/assets/solar-ac-prep.jpg?w=640;960;1280&format=avif;webp;jpg&as=picture";
// Video poster: one small WebP, since `poster` cannot take a srcset.
import interiorPoster from "@/assets/panel-interior.jpg?w=720&format=webp&quality=72";
// Blur-up placeholders for the comparison slider: ~1KB each, inlined by Vite.
import galleryBeforeTiny from "@/assets/gallery-before.jpg?w=24&format=webp&quality=45";
import galleryAfterTiny from "@/assets/gallery-after.jpg?w=24&format=webp&quality=45";

export type Picture = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

export const pictures = {
  hero,
  heroDaylight,
  heroAlt,
  interior,
  ev,
  fuse,
  upgrade,
  meter: meterMast,
  meterMast,
  conduit,
  repair,
  solarAc,
  team,
  burke,
  galleryBefore,
  galleryAfter,
} as const satisfies Record<string, Picture>;

export type ImageKey = keyof typeof pictures;

/** Single-file stills used where only one URL is allowed (video posters). */
export const posters = {
  interior: interiorPoster,
} as const;

/** Tiny blurred stills used behind lazily loaded photos. */
export const placeholders = {
  galleryBefore: galleryBeforeTiny,
  galleryAfter: galleryAfterTiny,
} as const;

export const images = Object.fromEntries(
  Object.entries(pictures).map(([key, value]) => [key, value.img.src]),
) as Record<ImageKey, string>;
/**
 * Default description for each photo, used wherever a page shows it without
 * its own wording (page banners), so no photo is ever left undescribed.
 */
const defaultAlts = new Map<Picture, string>([
  [hero, "Open breaker panel with its door swung back on a garage wall"],
  [heroDaylight, "Open residential breaker panel on a bright garage wall"],
  [heroAlt, "Newly installed breaker panel with neatly routed wiring in a garage"],
  [interior, "Interior of a modern electrical panel with labeled breakers"],
  [ev, "EV charger mounted beside an electrical panel in a home garage"],
  [fuse, "Old fuse box with screw-in fuses and worn wiring"],
  [upgrade, "Gray electrical panel and meter on the brick wall of a home"],
  [meterMast, "Electric meter and service mast on the side of a house"],
  [conduit, "Trench with underground electrical conduit beside a lawn"],
  [repair, "Electrician testing a breaker panel with a multimeter"],
  [solarAc, "Outdoor disconnects and conduit beside an air conditioner on a home"],
  [team, "Two Electrical Panels Only electricians beside the company service van"],
  [
    burke,
    "Burke, President and Master Electrician of Electrical Panels Only, beside the company service van",
  ],
  [galleryBefore, "Old rusted fuse panel with tangled wiring before replacement"],
  [galleryAfter, "Newly installed breaker panel with neat organized wiring after replacement"],
]);

export function pictureAlt(picture: Picture): string {
  return defaultAlts.get(picture) ?? "Electrical panel work by Electrical Panels Only";
}
